import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const isValidDate = (date: any) => {
  // @ts-ignore
  return date instanceof Date && !isNaN(date);
}

export type DateFormat = 'date' | 'time' | 'datetime' | 'year' | 'datetime_short_year';

export const formatDate = (date: Date, timezoneOffset: number, format: DateFormat = 'datetime'): string => {
  // date уже в UTC (парсится через parseStringAsUTCDate)
  // timezoneOffset - это offset таймзоны пользователя в минутах
  // Применяем offset пользователя к UTC дате для отображения в таймзоне пользователя
  const dateWithUserTimezone = new Date(date.getTime() + timezoneOffset * 60000);
  
  let dd: number | string = dateWithUserTimezone.getUTCDate();
  if (dd < 10) dd = '0' + dd;

  let mm: number | string = dateWithUserTimezone.getUTCMonth() + 1;
  if (mm < 10) mm = '0' + mm;

  let yyyy: number = dateWithUserTimezone.getUTCFullYear();

  let hh: number | string = dateWithUserTimezone.getUTCHours();
  if (hh < 10) hh = '0' + hh;

  let minutes: number | string = dateWithUserTimezone.getUTCMinutes();
  if (minutes < 10) minutes = '0' + minutes;

  switch (format) {
    case "year":
      return yyyy.toString();
    case "date":
      return dd + '.' + mm + '.' + yyyy;
    case "time":
      return hh + ':' + minutes;
    case "datetime_short_year":
      return dd + '.' + mm + '.' + (yyyy % 100) + ' ' + hh + ':' + minutes;
    default:
      return dd + '.' + mm + '.' + yyyy + ' ' + hh + ':' + minutes;
  }
}

export const parseStringAsUTCDate = (dateString: string) => {
  //for safari support
  const dateAndTime = dateString.split(' ');
  const formattedDateTimeString = dateAndTime[0] + "T" + dateAndTime[1] + ".000Z";
  return  new Date(Date.parse(formattedDateTimeString));
}

// Converts Date object to ISO string in UTC format
// Date object stores time internally as UTC timestamp, so we use UTC methods to format it correctly
export const getLocalDateAsISOString = (date: Date) => {
  const pad = (num: number) => {
    return (num < 10 ? '0' : '') + num;
  };

  return date.getUTCFullYear() +
    '-' + pad(date.getUTCMonth() + 1) +
    '-' + pad(date.getUTCDate()) +
    'T' + pad(date.getUTCHours()) +
    ':' + pad(date.getUTCMinutes()) +
    ':' + pad(date.getUTCSeconds()) +
    '.000Z'
}

/**
 * Converts a Dayjs object to a Date object, interpreting the Dayjs components as UTC
 * This is necessary when Dayjs objects are created from user input in UTC forms
 * (where the user enters UTC time, but Dayjs creates it as local time)
 * @param dayjsValue - Dayjs object (may be in local time, but components should be interpreted as UTC)
 * @returns Date object representing the UTC time
 */
export const dayjsToUTCDate = (dayjsValue: any): Date => {
  if (!dayjsValue) {
    return null as any;
  }
  
  // If already UTC, convert directly
  if (dayjsValue.isUTC && dayjsValue.isUTC()) {
    return dayjsValue.toDate();
  }
  
  // Otherwise, interpret components as UTC (same logic as LocalTimeDisplay)
  const dateString = `${dayjsValue.year()}-${String(dayjsValue.month() + 1).padStart(2, '0')}-${String(dayjsValue.date()).padStart(2, '0')} ${String(dayjsValue.hour()).padStart(2, '0')}:${String(dayjsValue.minute()).padStart(2, '0')}:${String(dayjsValue.second()).padStart(2, '0')}`;
  const utcMoment = dayjs.utc(dateString, 'YYYY-MM-DD HH:mm:ss');
  return utcMoment.toDate();
}

/**
 * Ensures range search always has both bounds. Uses exact datetimes — no full-day expansion.
 * Intersection on the backend: flight.start <= searchEnd AND flight.end >= searchStart.
 */
export function normalizeEmptyLegSearchDateRange(
  fromDate?: Date,
  toDate?: Date,
): { fromDate?: Date; toDate?: Date } {
  if (!fromDate) {
    return { fromDate: undefined, toDate: toDate ?? undefined };
  }

  return {
    fromDate,
    toDate: toDate ?? fromDate,
  };
}

export const dayjsWithOffset = (date: Date, offset: number) => {
  // first offset - is our local offset, second is custom
  const dateWithOffset = new Date(date.getTime() + date.getTimezoneOffset() * 60000 + offset * 60000);
  return dayjs(dateWithOffset)
}

/**
 * Проверяет, является ли дата сегодняшней или завтрашней
 * @param date - дата для проверки
 * @param timezoneOffset - смещение часового пояса
 * @returns true, если дата сегодняшняя или завтрашняя
 */
export const isDateTodayOrTomorrow = (date: Date, timezoneOffset: number): boolean => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
  
  // Применяем смещение часового пояса к дате
  const dateWithOffset = new Date(date.getTime() + timezoneOffset * 60000);
  const dateOnly = new Date(dateWithOffset.getFullYear(), dateWithOffset.getMonth(), dateWithOffset.getDate());
  
  return dateOnly.getTime() === today.getTime() || dateOnly.getTime() === tomorrow.getTime();
}